import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { CreateAudioButton } from '@/components/common/create-audio-button';
import { formatDateSmart } from '@/utils/date-formatter';
import {
  PlayIcon,
  PlayLoadingIcon,
  PlayingIcon,
  FavoriteIcon,
  FavoriteHoverIcon,
  FavoriteActiveIcon,
  CheckCircleActiveIcon,
  CheckCircleIcon,
  CheckCircleHoverIcon,
} from "@/components/ui/icon";
import { AudioPlayState } from '@/manager/manager/audio-controller-manager';
import { cn } from '@/lib/utils';
import { TruncatedText } from '@/components/common/truncated-text';
import { useTranslation } from 'react-i18next';
import { TextEditable } from '../common/text-editable';

// 骨架屏容器组件
function Skeleton() {
  return Array.from({ length: 14 }).map((_, index) => (
    <div key={index} className="w-full h-18 rounded-sm animate-pulse">
      <div className="w-full h-18 bg-gray-300 rounded-sm mb-4"></div>
    </div>
  ));
}

// 添加ref接口
export interface AudioDataCardRef {
  scrollToCurrentAudio: () => void;
}

interface AudioDataCardProps {
  audios: Audio[];
  selectAudio?: Audio;
  playingAudio?: Audio;
  currentTime: number;
  duration: number;
  playState: AudioPlayState;
  loading: boolean;
  onVoiceRename: (voice: Audio, name: string) => void;
  onVoiceAdd: (voice: Audio) => void;
  onVoiceSelect: (voice: Audio) => void;
  onPlayPause: (voice: Audio) => void;
  onToggleFavorite: (voice: Audio) => void;
  ref?: React.RefObject<HTMLVideoElement>;
}

/** my_voices 的音频数据卡片 */
export const AudioDataCard = forwardRef<AudioDataCardRef, AudioDataCardProps>((props, ref) => {
  const {
    audios,
    selectAudio,
    playingAudio,
    currentTime,
    duration,
    playState,
    loading,
    onVoiceRename,
    onVoiceAdd,
    onVoiceSelect,
    onPlayPause,
    onToggleFavorite,
  } = props;
  const { t } = useTranslation();
  const audioContainerRef = useRef<HTMLDivElement>(null);
  // 暴露滚动到当前音频的方法
  useImperativeHandle(ref, () => ({
    scrollToCurrentAudio: () => {
      if (!audioContainerRef.current) {
        return;
      }
      const selectors = `div[data-audio-id="${selectAudio?.id}"]`;
      const audioRow = audioContainerRef.current.querySelector(selectors);

      if (audioRow) {
        audioRow.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });

        // 添加高亮效果
        audioRow.classList.add("bg-i-active");
        setTimeout(() => {
          audioRow.classList.remove("bg-i-active");
        }, 2000);
      }
    },
  }));

  return (
    <div className='relative h-full w-full overflow-auto'>
      <div
        ref={audioContainerRef}
        className='grid grid-cols-2 gap-3 w-full mb-5'
      >
        {loading && <Skeleton />}
        {loading || (
          <>
            <CreateAudioButton
              className='w-full h-18'
              textClassName="text-sm"
              onSubmit={onVoiceAdd}
            />
            {audios.map((audio, index) => {
              const isCurrent = selectAudio?.id === audio.id;
              const isPlaying =
                playingAudio?.id === audio.id &&
                playState === AudioPlayState.PLAYING;
              const isLoading =
                playingAudio?.id === audio.id &&
                playState === AudioPlayState.LOADING;

              return (
                <div
                  key={`${audio.id}_${index}`}
                  data-audio-id={audio.id}
                  className='w-full h-18 flex justify-between items-center px-4 border border-[#EAECF0] rounded-[8px] cursor-pointer hover:bg-[#FCFCFD]'
                  onClick={() => onVoiceSelect(audio)}
                >
                  <div className='flex-1 min-w-0'>
                    <div className='text-sm font-medium mb-1'>
                      <TextEditable
                        className="p-0 w-full max-w-44 rounded-none"
                        value={audio.name}
                        mode="doubleClick"
                        onChange={(name) => onVoiceRename(audio, name)}
                      />
                    </div>
                    <div className='text-xs text-[#667085]'>{formatDateSmart(audio.createdAt).replace("Today", t("creation_folder.project_card.today"))}</div>
                  </div>
                  <div className="flex items-center justify-center gap-2 whitespace-nowrap">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onPlayPause(audio);
                      }}
                      className="p-1 hover:bg-gray-200 rounded transition-colors"
                    >
                      {isLoading ? (
                        <PlayLoadingIcon className="w-5 h-5" />
                      ) : isPlaying ? (
                        <PlayingIcon
                          className="w-5 h-5"
                          progress={Math.ceil(
                            (currentTime / duration) * 100
                          )}
                        />
                      ) : (
                        <PlayIcon className="w-5 h-5" />
                      )}
                    </button>
                    {/* <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggleFavorite(audio);
                      }}
                      className={cn(
                        "group p-1 rounded transition-colors hover:bg-gray-200 cursor-pointer"
                      )}
                    >
                      {audio.isStarred ? (
                        <FavoriteActiveIcon className="w-5 h-5" />
                      ) : (
                        <>
                          <FavoriteIcon className="group-hover:hidden w-5 h-5" />
                          <FavoriteHoverIcon className="hidden group-hover:block w-5 h-5" />
                        </>
                      )}
                    </button> */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onVoiceSelect(audio);
                      }}
                      className={cn(
                        "group p-1 rounded transition-colors hover:bg-gray-200 cursor-pointer"
                      )}
                    >
                      {isCurrent ? (
                        <CheckCircleActiveIcon className="w-5 h-5" />
                      ) : (
                        <>
                          <CheckCircleIcon className="group-hover:hidden w-5 h-5" />
                          <CheckCircleHoverIcon className="hidden group-hover:block w-5 h-5" />
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )
            })}
          </>
        )}
      </div>
    </div>
  )
})
